import { useCallback, useRef, useState } from "react"

import { sendCopy } from "@/modules/helpers/utils"

/**
 * Hook to handle copy to clipboard with timeout state management
 * @param timeoutDuration - Duration in ms before resetting copied state (default: 1400ms)
 * @returns Object with copied state and copy handler
 */
export function useCopyToClipboard(timeoutDuration: number = 1400) {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const copy = useCallback(
    (text: string) => {
      if (!text) return

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Copy to clipboard
      sendCopy(text)
      setCopied(true)

      // Reset copied state after timeout
      timeoutRef.current = setTimeout(() => {
        setCopied(false)
        timeoutRef.current = null
      }, timeoutDuration)
    },
    [timeoutDuration]
  )

  return { copied, copy }
}

/**
 * Hook to handle multiple copy states (e.g., copy link and copy hashtag)
 * @param keys - Array of keys to track copy states for
 * @param timeoutDuration - Duration in ms before resetting copied state (default: 1400ms)
 * @returns Object with copied states and copy handler
 */
export function useMultipleCopyToClipboard<T extends string>(
  keys: T[],
  timeoutDuration: number = 1400
) {
  const [copiedStates, setCopiedStates] = useState<Record<T, boolean>>(
    keys.reduce((acc, key) => ({ ...acc, [key]: false }), {} as Record<T, boolean>)
  )
  const timeoutRefs = useRef<Record<T, NodeJS.Timeout | null>>(
    keys.reduce((acc, key) => ({ ...acc, [key]: null }), {} as Record<T, NodeJS.Timeout | null>)
  )

  const copy = useCallback(
    (key: T, text: string) => {
      if (!text) return

      // Clear existing timeout for this key
      if (timeoutRefs.current[key]) {
        clearTimeout(timeoutRefs.current[key]!)
      }

      // Copy to clipboard
      sendCopy(text)
      setCopiedStates((prev) => ({ ...prev, [key]: true }))

      // Reset copied state after timeout
      timeoutRefs.current[key] = setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [key]: false }))
        timeoutRefs.current[key] = null
      }, timeoutDuration)
    },
    [timeoutDuration]
  )

  return { copiedStates, copy }
}
